import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { ThemeName } from "@/types/theme";
import { getThemeFromStorage } from "@/contexts/ThemeContext";

export type SourceChannel = "site" | "siga-facil" | "zapay";

/**
 * Função helper para detectar tema a partir de uma URL
 * Pode ser usada tanto com window.location quanto com location do react-router
 */
export const detectThemeFromLocation = (location: {
  hash: string;
  search: string;
  pathname: string;
}): ThemeName => {
  // 1. Verifica hash da URL (#siga-facil, #zapay)
  const hash = location.hash.toLowerCase();
  if (hash.includes("siga-facil")) {
    return "siga-facil";
  }
  if (hash.includes("zapay")) {
    return "zapay";
  }

  // 2. Verifica query parameter (?theme=siga-facil)
  const searchParams = new URLSearchParams(location.search);
  const themeParam = searchParams.get("theme");
  if (
    themeParam &&
    ["siga-facil", "zapay", "default"].includes(themeParam.toLowerCase())
  ) {
    return themeParam.toLowerCase() as ThemeName;
  }

  // 3. Verifica pathname (/siga-facil, /zapay)
  const pathname = location.pathname.toLowerCase();
  if (pathname.includes("/siga-facil")) {
    return "siga-facil";
  }
  if (pathname.includes("/zapay")) {
    return "zapay";
  }

  // 4. Tema padrão
  return "default";
};

export function resolveSourceChannel(options?: {
  location?: { hash: string; search: string; pathname: string };
  storedTheme?: ThemeName;
  company?: string;
}): SourceChannel {
  const location =
    options?.location ??
    (typeof window !== "undefined"
      ? {
          hash: window.location.hash,
          search: window.location.search,
          pathname: window.location.pathname,
        }
      : { hash: "", search: "", pathname: "" });

  const theme = detectThemeFromLocation(location);
  const storedTheme = options?.storedTheme ?? getThemeFromStorage();
  const company = options?.company?.toLowerCase();

  if (
    theme === "siga-facil" ||
    storedTheme === "siga-facil" ||
    company === "sigafacil" ||
    company === "siga-facil"
  ) {
    return "siga-facil";
  }

  if (theme === "zapay" || storedTheme === "zapay" || company === "zapay") {
    return "zapay";
  }

  return "site";
}

/**
 * Hook para detectar o tema baseado na URL
 *
 * Detecta o tema através de:
 * 1. Hash da URL (#siga-facil, #zapay)
 * 2. Query parameter (?theme=siga-facil)
 * 3. Pathname (/siga-facil, /zapay)
 *
 * IMPORTANTE: Este hook deve ser usado apenas dentro de um componente Router
 *
 * @returns ThemeName - Nome do tema detectado
 */
export const useTheme = (): ThemeName => {
  const location = useLocation();

  return useMemo(() => {
    return detectThemeFromLocation(location);
  }, [location]);
};
